/**
 * Sidebar panel for paying an invoice.
 *
 * Fetches payment options (supported types + saved methods) on open,
 * lets the user pick a payment method and amount (partial payments
 * allowed), then submits for immediate capture via the portal API.
 * Consistent UX with the cart checkout payment flow.
 */

import {
	faCreditCard,
	faPlus,
	faWallet,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaymentMethodTypeEnum } from '@supply-mover-2-0/core';
import type {
	PaymentDecline,
	PortalCheckoutPaymentMethodDto,
	SupportedPaymentMethodType,
} from '@supply-mover-2-0/shared';
import { ACCOUNT_FUNDS_METHOD_TYPE } from '@supply-mover-2-0/shared';
import {
	extractPaymentDecline,
	PaymentDeclineModal,
	Sidebar,
	Toast,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { toCustomerDecline } from '../lib/customerDecline';
import { formatCurrency } from '../lib/formatCurrency';
import { triggerLightImpact } from '../lib/haptics';
import * as portalInvoiceService from '../services/portalInvoiceService';
import { PortalAddPaymentMethod } from './PortalAddPaymentMethod';

interface PayInvoiceSidebarProps {
	isOpen: boolean;
	onClose: () => void;
	invoiceId: number;
	outstandingBalance: number;
	invoiceExternalId: string | null;
	onPaymentSuccess: (updatedBalance: string) => void;
}

export const PayInvoiceSidebar: React.FC<PayInvoiceSidebarProps> = ({
	isOpen,
	onClose,
	invoiceId,
	outstandingBalance,
	invoiceExternalId,
	onPaymentSuccess,
}) => {
	const { t } = useTranslation();
	const { token } = usePortalAuth();

	// Payment options from server
	const [paymentTypes, setPaymentTypes] = useState<
		Array<SupportedPaymentMethodType>
	>([]);
	const [savedMethods, setSavedMethods] = useState<
		Array<PortalCheckoutPaymentMethodDto>
	>([]);
	// PD-15295 — the invoice client's account-funds position; drives the
	// Account Funds payment type row and caps the amount.
	const [accountFunds, setAccountFunds] = useState<{
		enabled: boolean;
		available: number;
	} | null>(null);
	const [optionsLoading, setOptionsLoading] = useState(false);

	// Form state
	const [amount, setAmount] = useState('');
	const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
	const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// PD-15550 — a gateway refusal is a dialog, not a line of inline text.
	// Held separately from `error` so the two never render at once.
	const [decline, setDecline] = useState<PaymentDecline | null>(null);

	// Add payment method sidebar (nested)
	const [showAddPayment, setShowAddPayment] = useState(false);

	/** Saved-method ids seen on the last fetch of THIS open session — a
	 *  refetch that surfaces a new id means the user just added that method
	 *  through the nested sidebar (PD-15014). */
	const knownMethodIdsRef = useRef<Set<number> | null>(null);

	// Fetch payment options when sidebar opens
	const fetchPaymentOptions = useCallback(async () => {
		if (token == null) {
			return;
		}

		setOptionsLoading(true);
		try {
			const res = await portalInvoiceService.getInvoicePaymentOptions(
				token,
				invoiceId,
			);
			if (res.success) {
				const types = res.data.paymentTypes;
				const methods = res.data.savedPaymentMethods;
				setPaymentTypes(types);
				setSavedMethods(methods);
				setAccountFunds(res.data.accountFunds ?? null);

				const prevKnown = knownMethodIdsRef.current;
				knownMethodIdsRef.current = new Set(methods.map((m) => m.id));
				const fresh =
					prevKnown == null ? null : methods.find((m) => !prevKnown.has(m.id));
				if (fresh != null) {
					// PD-15014 — a method added mid-flow is the one the user means
					// to pay with: select it and switch to its type.
					const freshType = types.find(
						(pt) => pt.paymentMethodType === fresh.type,
					);
					if (freshType != null) {
						setSelectedTypeId(freshType.id);
					}
					setSelectedMethodId(fresh.id);
				} else {
					// Auto-select first payment type
					if (types.length > 0) {
						setSelectedTypeId(types[0].id);
					}

					// Auto-select primary saved method
					const primary = methods.find((m) => m.isPrimary);
					if (primary != null) {
						setSelectedMethodId(primary.id);
					} else if (methods.length > 0) {
						setSelectedMethodId(methods[0].id);
					}
				}
			}
		} catch (err) {
			console.error('Failed to fetch payment options:', err);
			setError(t('Failed to load payment options'));
		} finally {
			setOptionsLoading(false);
		}
	}, [token, invoiceId, t]);

	useEffect(() => {
		if (isOpen) {
			// Reset state when opening (a fresh baseline for the just-added
			// method detection too — ids from a previous open don't count).
			knownMethodIdsRef.current = null;
			setAmount(outstandingBalance.toFixed(2));
			setError(null);
			setDecline(null);
			setSubmitting(false);
			void fetchPaymentOptions();
		}
	}, [isOpen, outstandingBalance, fetchPaymentOptions]);

	// Currently selected payment type object
	const selectedType = paymentTypes.find((pt) => pt.id === selectedTypeId);

	// PD-15295 — Account Funds spends the account's own balance: no saved
	// method applies, and the amount is capped by the balance as well as the
	// outstanding figure.
	const isFundsSelected =
		selectedType?.paymentMethodType === ACCOUNT_FUNDS_METHOD_TYPE;
	const fundsAvailable = accountFunds?.available ?? 0;

	// Filter saved methods to match the selected payment type
	const methodsForType = savedMethods.filter(
		(m) => m.type === selectedType?.paymentMethodType,
	);

	// One label helper so the grouped and flat renderings can't drift apart.
	const methodLabel = (m: PortalCheckoutPaymentMethodDto): string =>
		[
			t(m.subtype ?? m.type),
			m.lastFour != null ? `****${m.lastFour}` : '',
			m.name != null ? `(${t(m.name)})` : '',
		]
			.filter((part) => part.length > 0)
			.join(' ');

	// Methods bucketed by the account they are stored on, preserving the
	// server's ordering (primary first, then newest) within each bucket.
	const methodOwnerGroups = methodsForType.reduce<
		Array<{
			ownerClientId: number;
			ownerClientName: string | null;
			methods: Array<PortalCheckoutPaymentMethodDto>;
		}>
	>((groups, m) => {
		const existing = groups.find((g) => g.ownerClientId === m.ownerClientId);
		if (existing != null) {
			existing.methods.push(m);
			return groups;
		}
		groups.push({
			ownerClientId: m.ownerClientId,
			ownerClientName: m.ownerClientName,
			methods: [m],
		});
		return groups;
	}, []);

	// Processing fee preview
	const parsedAmount = parseFloat(amount);
	const maxPayable = isFundsSelected
		? Math.min(outstandingBalance, fundsAvailable)
		: outstandingBalance;
	const isValidAmount =
		!isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount <= maxPayable;
	// PD-15173 — the fee depends on the CARD, not just its type. Only a credit
	// card may be surcharged: Regulation II prohibits surcharging debit, and the
	// networks extend that to prepaid, so the server takes nothing on those. This
	// quoted the type's rate whatever the customer picked, which showed a total
	// they were never charged. `isSurchargeable` is resolved server-side, so this
	// stays true for any future method type with its own eligibility rules.
	const selectedMethod = savedMethods.find((m) => m.id === selectedMethodId);
	const feeApplies = selectedMethod == null || selectedMethod.isSurchargeable;
	const feeAmount =
		selectedType != null && isValidAmount && feeApplies
			? selectedType.processingFeeType === '%'
				? // Floored, matching what the server charges (PD-15174): a rate
					// sitting on the surcharge cap must never round above it.
					Math.floor(parsedAmount * selectedType.processingFee) / 100
				: selectedType.processingFee
			: 0;
	const totalWithFee = isValidAmount ? parsedAmount + feeAmount : 0;

	const canSubmit =
		isValidAmount &&
		selectedTypeId != null &&
		// An Account Funds payment has no saved-method row to select.
		(isFundsSelected || selectedMethodId != null) &&
		!submitting;

	const handleSubmit = async (): Promise<void> => {
		if (token == null || !canSubmit) {
			return;
		}

		setSubmitting(true);
		setError(null);
		setDecline(null);

		try {
			const res = await portalInvoiceService.payInvoice(
				token,
				invoiceId,
				isFundsSelected
					? {
							paymentAmount: parsedAmount,
							paymentMethodType: ACCOUNT_FUNDS_METHOD_TYPE,
						}
					: {
							paymentAmount: parsedAmount,
							paymentMethodId: selectedMethodId ?? undefined,
						},
			);

			if (res.success) {
				// PD-14695: a light tap confirms the payment landed.
				triggerLightImpact();

				// PD-15550 — success closes this sidebar, so without a toast the
				// customer is returned to the invoice with nothing on screen
				// saying the payment went through. State the amount actually
				// taken (a surcharged card is charged the total, not the payment
				// figure they typed) and what the invoice owes afterwards, so the
				// confirmation answers both questions the receipt would.
				const chargedAmount = feeAmount > 0 ? totalWithFee : parsedAmount;
				const invoiceRef = invoiceExternalId ?? String(invoiceId);
				const paidText = t(
					`${formatCurrency(chargedAmount) ?? ''} paid on invoice #${invoiceRef}`,
				);
				// The server returns the new balance as a string; if it ever fails
				// to parse, say nothing about the remainder rather than "NaN".
				const remainingBalance = parseFloat(res.updatedOutstandingBalance);
				const remainingText = Number.isNaN(remainingBalance)
					? null
					: remainingBalance <= 0
						? t('Invoice paid in full')
						: t(`Remaining balance: ${formatCurrency(remainingBalance) ?? ''}`);

				Toast.show({
					type: 'success',
					text1: t('Payment submitted'),
					text2:
						remainingText == null
							? paidText
							: `${paidText} ${t('·')} ${remainingText}`,
					duration: 5000,
				});

				onPaymentSuccess(res.updatedOutstandingBalance);
				onClose();
			}
		} catch (err) {
			// PD-15550 — a refusal from the gateway is presented as a dialog the
			// customer has to acknowledge, matching every other portal payment
			// surface (PD-15185). The sidebar stays open behind it so they can
			// pick a different method without reopening the flow. Anything that
			// is not a decline (a network drop, a validation error) is still the
			// inline message: dressing those as a decline would misreport them.
			const paymentDecline = toCustomerDecline(extractPaymentDecline(err));

			if (paymentDecline != null) {
				setDecline(paymentDecline);
			} else {
				const message =
					err instanceof Error
						? err.message
						: t('Payment failed. Please try again.');
				setError(message);
			}
		} finally {
			setSubmitting(false);
		}
	};

	// After adding a new payment method, refresh options and auto-select it
	const handleAddPaymentSuccess = (): void => {
		setShowAddPayment(false);
		void fetchPaymentOptions();
	};

	return (
		<>
			<Sidebar
				isOpen={isOpen}
				onClose={onClose}
				title={t('Pay Invoice')}
				width="md"
				position="right"
				// The shared Sidebar defaults to the CRM theme's surface, which
				// has no knowledge of the tenant brand — against a branded page
				// it read as a different product. Paint it on the storefront's
				// own surface token instead (PD-15016).
				panelClassName="bg-brand-surface"
				modal={true}
			>
				<div className="p-4">
					{/* Invoice reference */}
					<div className="mb-4 pb-4 border-b border-brand-border">
						<p className="text-sm text-brand-muted">
							{t('Invoice')} #{t(invoiceExternalId ?? String(invoiceId))}
						</p>
						<p className="text-lg font-bold text-default">
							{t('Outstanding')}: {t(formatCurrency(outstandingBalance) ?? '')}
						</p>
					</div>

					{optionsLoading && (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-portal-accent" />
						</div>
					)}

					{!optionsLoading && (
						<>
							{/* Amount */}
							<div className="mb-4">
								<label className="block text-sm font-medium text-strong mb-1">
									{t('Payment Amount')}
								</label>
								<div className="relative">
									<span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted">
										$
									</span>
									<input
										type="number"
										value={amount}
										onChange={(e) => {
											setAmount(e.target.value);
											setError(null);
										}}
										min="0.01"
										max={maxPayable}
										step="0.01"
										className="w-full pl-7 pr-3 py-2.5 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
									/>
								</div>
								{!isNaN(parsedAmount) && parsedAmount > outstandingBalance && (
									<p className="text-xs text-danger mt-1">
										{t('Cannot exceed outstanding balance')}
									</p>
								)}
								{isFundsSelected &&
									!isNaN(parsedAmount) &&
									parsedAmount <= outstandingBalance &&
									parsedAmount > fundsAvailable && (
										<p className="text-xs text-danger mt-1">
											{t('Cannot exceed available account funds')}
										</p>
									)}
							</div>

							{/* Payment type */}
							{paymentTypes.length > 0 && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-strong mb-2">
										{t('Payment Type')}
									</label>
									<div className="space-y-2">
										{paymentTypes.map((pt) => {
											// PD-15173 — the type's rate is the honest answer until a
											// card is chosen, but once one is, the row must say what
											// THAT card pays. A card that cannot be surcharged pays
											// nothing, so it reads "No fee" rather than continuing to
											// advertise the type's rate. Mirrors the cart checkout.
											const rowFee =
												pt.id === selectedTypeId && !feeApplies
													? 0
													: pt.processingFee;

											return (
												<label
													key={pt.id}
													className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input"
												>
													<input
														type="radio"
														name="invoicePaymentType"
														checked={selectedTypeId === pt.id}
														onChange={() => {
															setSelectedTypeId(pt.id);
															// Auto-select first method for this type
															const methods = savedMethods.filter(
																(m) => m.type === pt.paymentMethodType,
															);
															const primary = methods.find((m) => m.isPrimary);
															// `.at(0)` rather than `[0]`: the index signature
															// is typed non-optional, so the compiler believed
															// the `?? null` was unreachable when an empty list
															// makes it the only thing standing between here and
															// an undefined selection.
															setSelectedMethodId(
																primary?.id ?? methods.at(0)?.id ?? null,
															);
														}}
														className="w-4 h-4 text-portal-accent"
													/>
													<FontAwesomeIcon
														icon={
															pt.paymentMethodType === ACCOUNT_FUNDS_METHOD_TYPE
																? faWallet
																: faCreditCard
														}
														className="text-default"
													/>
													<span className="text-sm text-default">
														{t(pt.paymentMethodType)}
													</span>
													{/* Label BOTH cases. Showing the surcharge only on the
												    types that carry one leaves the others ambiguous —
												    silence reads as "unknown", not "free" — so a
												    fee-free option gets no credit for being cheaper.
												    Stating "No fee" makes the comparison explicit and
												    nudges customers toward it (customer feedback). */}
													<span
														className={`text-xs ml-auto ${
															rowFee > 0
																? 'text-brand-muted'
																: 'text-portal-accent font-medium'
														}`}
													>
														{/* The funds row states the balance being spent —
														    more useful than "No fee", which is implied. */}
														{pt.paymentMethodType === ACCOUNT_FUNDS_METHOD_TYPE
															? `${formatCurrency(fundsAvailable) ?? ''} ${t('available')}`
															: rowFee > 0
																? pt.processingFeeType === '%'
																	? `${rowFee}% ${t('fee')}`
																	: `${formatCurrency(rowFee)} ${t('fee')}`
																: t('No fee')}
													</span>
												</label>
											);
										})}
									</div>
								</div>
							)}

							{/* Saved payment methods — not applicable to Account Funds,
							    which has no method row to choose. */}
							{selectedTypeId != null && !isFundsSelected && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-strong mb-1">
										{t('Payment Method')}
									</label>
									{methodsForType.length > 0 ? (
										<select
											value={selectedMethodId ?? ''}
											onChange={(e) =>
												setSelectedMethodId(parseInt(e.target.value, 10))
											}
											className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
										>
											{/* Grouped by owning account. A user assigned to a parent
											    can pay a child's invoice with either account's card,
											    so a flat list can show two methods ending in the same
											    four digits with no way to tell them apart. Only group
											    when more than one account is actually represented —
											    a single-account customer gets no useless header. */}
											{methodOwnerGroups.length > 1
												? methodOwnerGroups.map((group) => (
														<optgroup
															key={group.ownerClientId}
															label={group.ownerClientName ?? t('Your account')}
														>
															{group.methods.map((m) => (
																<option key={m.id} value={m.id}>
																	{methodLabel(m)}
																</option>
															))}
														</optgroup>
													))
												: methodsForType.map((m) => (
														<option key={m.id} value={m.id}>
															{methodLabel(m)}
														</option>
													))}
										</select>
									) : (
										<p className="text-xs text-brand-muted py-1">
											{t('No saved payment methods for this type.')}
										</p>
									)}
									<button
										type="button"
										onClick={() => setShowAddPayment(true)}
										className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:underline mt-2"
									>
										<FontAwesomeIcon icon={faPlus} />
										<span>{t('Add new payment method')}</span>
									</button>
								</div>
							)}

							{/* Fee preview */}
							{feeAmount > 0 && isValidAmount && (
								<div className="mb-4 p-3 bg-brand-input rounded-lg">
									<div className="flex justify-between text-sm">
										<span className="text-brand-muted">{t('Payment')}</span>
										<span className="text-default">
											{t(formatCurrency(parsedAmount) ?? '')}
										</span>
									</div>
									<div className="flex justify-between text-sm mt-1">
										<span className="text-brand-muted">
											{t('Processing fee')}
										</span>
										<span className="text-default">
											{t(formatCurrency(feeAmount) ?? '')}
										</span>
									</div>
									<div className="flex justify-between text-sm font-semibold mt-1 pt-1 border-t border-brand-border">
										<span className="text-default">{t('Total charge')}</span>
										<span className="text-default">
											{t(formatCurrency(totalWithFee) ?? '')}
										</span>
									</div>
								</div>
							)}

							{/* Error */}
							{error != null && (
								<div className="mb-4 p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
									<p className="text-sm text-danger">{t(error)}</p>
								</div>
							)}

							{/* Submit */}
							<button
								type="button"
								onClick={() => void handleSubmit()}
								disabled={!canSubmit}
								className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:bg-control-strong disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
							>
								{submitting ? (
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
								) : null}
								{submitting
									? t('Processing...')
									: t(
											`Pay ${formatCurrency(isValidAmount ? parsedAmount : 0)}`,
										)}
							</button>
						</>
					)}
				</div>
			</Sidebar>

			{/* Nested add payment method sidebar */}
			<Sidebar
				isOpen={showAddPayment}
				onClose={() => setShowAddPayment(false)}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				// The shared Sidebar defaults to the CRM theme's surface, which
				// has no knowledge of the tenant brand — against a branded page
				// it read as a different product. Paint it on the storefront's
				// own surface token instead (PD-15016).
				panelClassName="bg-brand-surface"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={handleAddPaymentSuccess}
						onCancel={() => setShowAddPayment(false)}
						supportedPaymentMethods={paymentTypes}
						preSelectedPaymentType={
							selectedType?.paymentMethodType === 'ACH'
								? PaymentMethodTypeEnum.ACH
								: selectedType?.paymentMethodType === 'Credit Card'
									? PaymentMethodTypeEnum.CREDIT_CARD
									: undefined
						}
					/>
				</div>
			</Sidebar>

			{/* PD-15550 — a refused payment, acknowledged deliberately. A sibling
			    of the Sidebars rather than a child, so it stacks above the open
			    panel and closing it leaves the panel exactly as it was. */}
			<PaymentDeclineModal
				decline={decline}
				onClose={(): void => {
					setDecline(null);
				}}
			/>
		</>
	);
};
